import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useRequest } from 'ahooks';
import { Spin, Empty, message } from 'antd';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/models/store';
import style from './index.module.less';
import xlsxSvg from './assets/xlsx.svg';
import zipSvg from './assets/zip.svg';
import pdfSvg from './assets/pdf.svg';
import { DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from '@/hooks/useTranslation';
import { getArticleDetail, getPreviewFile, type FileInfoVo } from '@/api/sip/notification';
import { data } from '@/models/notificationData';
import { report } from '@/utils/report';
import { formatNoticeTime } from '@/utils/formatTime';

const NOTICE_DETAIL_PAGE_ID = 'P20393';
const NOTICE_DETAIL_VIEW_BUTTON_ID = 'B001';

function getFileTypeSvgByExt(fileName: string) {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  switch (ext) {
    case 'xlsx':
    case 'xls':
      return xlsxSvg;
    case 'zip':
      return zipSvg;
    case 'pdf':
      return pdfSvg;
    default:
      return '';
  }
}

function getFileTypeSvgByType(type: string) {
  switch (type) {
    case 'xlsx':
      return xlsxSvg;
    case 'zip':
      return zipSvg;
    case 'pdf':
      return pdfSvg;
    default:
      return '';
  }
}

/**
 * 国内（isGlobal=false）—— 用写死数据 `data`，按 numeric id 匹配。
 * 与 develop 分支实现保持一致：附件下载直接 window.open(item.url)。
 */
const HardcodedDetail = ({ noticeId }: { noticeId: string }) => {
  const $ = useTranslation();
  const dataSource = useMemo(
    () => data.find(item => item.id === Number(noticeId)) || null,
    [noticeId]
  );

  return (
    <div className={style.container}>
      <div className={style.title}>{dataSource?.title}</div>
      <div className={style.time}>{dataSource?.time}</div>
      <div className={style.line} />
      <div
        className={style.content}
        dangerouslySetInnerHTML={{ __html: dataSource?.content ?? '' }}
      />
      {dataSource?.assets && dataSource.assets.length > 0 && (
        <>
          <div className={style.subtitle}>{$('附件')}</div>
          <div className={style.assets}>
            {dataSource.assets.map((item: any, index: number) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} className={style['assets-item']}>
                <span className={style['assets-item-bd']}>
                  <img
                    src={getFileTypeSvgByType(item.type)}
                    alt={item.name}
                    className={style['assets-item-icon']}
                  />
                  <a
                    href={item.url}
                    target='_blank'
                    rel='noreferrer'
                    className={style['assets-item-name']}
                  >
                    {item.name}
                  </a>
                  <DownloadOutlined
                    style={{ fontSize: '16px', color: '#0068FF', cursor: 'pointer' }}
                    onClick={() => {
                      window.open(item.url, '_blank');
                    }}
                  />
                </span>
                {item.date && <span className={style['assets-item-date']}>{item.date}</span>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * 国际（isGlobal=true）—— 走接口：`getArticleDetail(articleUuid)` 拉详情，
 * 附件下载走 `getPreviewFile({ fileId })` 拿签名 URL 后 window.open。
 */
const ApiDetail = ({ noticeId }: { noticeId: string }) => {
  const $ = useTranslation();
  const countryCode = useSelector<RootState, string>(
    state => state.LocaleModel.countryCode,
    shallowEqual
  );
  const userId = useSelector<RootState, string | number>(
    state => state.UserModel.currentUser?.uid ?? '',
    shallowEqual
  );

  const { data: detail, loading } = useRequest(
    async () => {
      if (!noticeId) return null;
      const res = await getArticleDetail(noticeId);
      if (res.code !== 200) return null;
      return res.data ?? null;
    },
    { refreshDeps: [noticeId] }
  );

  useEffect(() => {
    if (!detail) return;
    report({
      data: {
        pageId: NOTICE_DETAIL_PAGE_ID,
        buttonId: NOTICE_DETAIL_VIEW_BUTTON_ID,
        countryOrRegion: countryCode,
        uuid: detail.articleUuid,
        text_lang: detail.lang,
        userId
      }
    });
  }, [detail, countryCode, userId]);

  const handleDownload = async (file: FileInfoVo) => {
    if (!file.fileId) {
      message.error($('下载失败'));
      return;
    }
    const res = await getPreviewFile({ fileId: file.fileId });
    if (res.code !== 200) {
      message.error(res.msg);
      return;
    }
    const url = res.data?.downloadUrl;
    if (!url) {
      message.error($('下载失败'));
      return;
    }
    window.open(url, '_blank');
  };

  return (
    <div className={style.container}>
      <Spin spinning={loading}>
        {detail ? (
          <>
            <div className={style.title}>{detail.title}</div>
            <div className={style.time}>{formatNoticeTime(detail.publishTime, countryCode)}</div>
            <div className={style.line} />
            {detail.summary && <div className={style.summary}>{detail.summary}</div>}
            <div
              className={style.content}
              dangerouslySetInnerHTML={{ __html: detail.textContent ?? '' }}
            />
            {detail.fileList && detail.fileList.length > 0 && (
              <>
                <div className={style.subtitle}>{$('附件')}</div>
                <div className={style.assets}>
                  {detail.fileList.map((item: FileInfoVo) => (
                    <div key={item.fileId} className={style['assets-item']}>
                      <span className={style['assets-item-bd']}>
                        <img
                          src={getFileTypeSvgByExt(item.fileName)}
                          alt={item.fileName}
                          className={style['assets-item-icon']}
                        />
                        <span
                          className={style['assets-item-name']}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleDownload(item)}
                        >
                          {item.fileName}
                        </span>
                        <DownloadOutlined
                          style={{ fontSize: '16px', color: '#0068FF', cursor: 'pointer' }}
                          onClick={() => handleDownload(item)}
                        />
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          !loading && <Empty description={$('暂无数据')} />
        )}
      </Spin>
    </div>
  );
};

/**
 * 顶层入口：按 `LocaleModel.isGlobal` 分发到对应实现。
 *   - 国内（`isGlobal=false`，`countryCode='CN'`）→ 走写死数据（HardcodedDetail）
 *   - 国际（`isGlobal=true`，非 CN）→ 走接口（ApiDetail）
 */
export default () => {
  const { noticeId } = useParams<{ noticeId: string }>();
  const isGlobal = useSelector<RootState, boolean>(
    state => state.LocaleModel.isGlobal,
    shallowEqual
  );
  return isGlobal ? (
    <ApiDetail noticeId={noticeId ?? ''} />
  ) : (
    <HardcodedDetail noticeId={noticeId ?? ''} />
  );
};
